import React from "react";
import { useTranslation } from "react-i18next";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import { Icon } from "@/components/Icon";
import { TaskCard } from "@/pages/Tasks/TaskCard";

export interface KanbanColumnProps {
    status: {
        id: number;
        tag: string;
        color?: string;
        is_hidden?: boolean;
        [key: string]: any;
    };
    tasks: any[];
    onAddTask: (statusId: number) => void;
    onTaskClick: (task: any) => void;
    onPriorityClick?: (task: any) => void;
    onAssigneeClick?: (task: any) => void;
    onSprintClick?: (task: any) => void;
    index: number;
    isDraggingRef: React.MutableRefObject<boolean>;
    dragEndedAtRef: React.MutableRefObject<number>;
}

export const KanbanColumn = ({
    status,
    tasks,
    onAddTask,
    onTaskClick,
    onPriorityClick,
    onAssigneeClick,
    onSprintClick,
    index,
    isDraggingRef,
    dragEndedAtRef,
}: KanbanColumnProps) => {
    const { t } = useTranslation("global");

    return (
        <Draggable draggableId={`col-${status.id}`} index={index}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className={`flex flex-col flex-1 min-w-[260px] bg-white dark:bg-zinc-900/50 border border-slate-200 dark:border-zinc-800 rounded-2xl h-full group/col transition-colors duration-200 ${snapshot.isDragging ? "opacity-80 scale-[1.02] shadow-xl z-50" : ""}`}
                    onMouseEnter={(e) => {
                        const color = status.color || "#cbd5e1";
                        e.currentTarget.style.borderColor = color;
                        e.currentTarget.style.boxShadow = `0 4px 20px -2px ${color}20`;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "";
                        e.currentTarget.style.boxShadow = "";
                    }}
                >
                    <div
                        {...provided.dragHandleProps}
                        className="bg-slate-100/50 flex items-center p-3 border-b border-slate-200/60 dark:border-zinc-800/60 transition-colors duration-200 group-hover/col:border-transparent cursor-grab active:cursor-grabbing"
                    >
                        <div
                            className="w-3 h-3 rounded-full mr-2 shrink-0"
                            style={{
                                backgroundColor: status.color || "#cbd5e1",
                            }}
                        />
                        <h3 className="font-bold text-slate-700 dark:text-zinc-200 uppercase tracking-wider text-xs">
                            {t(`tasks.statuses.${status.tag}` || status.name)}
                        </h3>

                        <button
                            type="button"
                            onClick={() => onAddTask(status.id)}
                            className="ml-auto mr-2 text-slate-400 hover:text-primary transition-colors opacity-0 group-hover/col:opacity-100 focus:opacity-100 cursor-pointer"
                            title={t("tasks.new_task")}
                        >
                            <Icon name="plus" className="w-4 h-4" />
                        </button>

                        <span className="bg-white dark:bg-zinc-800 text-slate-600 dark:text-slate-400 text-xs py-0.5 px-2.5 rounded-full font-medium border border-slate-200 dark:border-zinc-700 shadow-sm">
                            {tasks.length}
                        </span>
                    </div>
                    <Droppable droppableId={String(status.id)}>
                        {(provided, snapshot) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                onClick={(e) => {
                                    if (
                                        isDraggingRef.current ||
                                        snapshot.isDraggingOver ||
                                        Date.now() - dragEndedAtRef.current <
                                            250
                                    ) {
                                        return;
                                    }

                                    if (e.target === e.currentTarget) {
                                        onAddTask(status.id);
                                    }
                                }}
                                className={`flex-1 p-2 overflow-y-auto overflow-x-hidden ${snapshot.isDraggingOver ? "bg-primary/5 dark:bg-primary/10 ring-2 ring-primary/30 ring-inset" : "cursor-pointer"} transition-colors custom-scrollbar`}
                            >
                                {tasks.map((task, index) => (
                                    <div
                                        key={task.id}
                                        onClick={(e) => e.stopPropagation()}
                                        className="cursor-default"
                                    >
                                        <TaskCard
                                            task={task}
                                            index={index}
                                            onClick={onTaskClick}
                                            onPriorityClick={onPriorityClick}
                                            onAssigneeClick={onAssigneeClick}
                                            onSprintClick={onSprintClick}
                                            columnColor={status.color}
                                        />
                                    </div>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </div>
            )}
        </Draggable>
    );
};
